import { useEffect, useRef } from 'react'
import { createRootRouteWithContext, Outlet, ErrorComponentProps, useRouterState } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import { useSelector } from 'react-redux'
import type { RouterContext } from '../router'
import { RouteErrorBoundary } from '@/components/error'
import { GradientBackground } from '@/components/ui'
import { OnboardingTour } from '@/components/onboarding'
import { UpgradePromptModal } from '@/modals/subscription'
import { useAppDispatch } from '@/store/store'
import { incrementGradient } from '@/store/slices/gradientSlice'
import { selectIsTourOpen, closeTour } from '@/store/slices/uiSlice'
import {
  selectIsUpgradeModalOpen,
  selectUpgradePromptContext,
  closeUpgradeModal,
} from '@/store/slices/subscriptionSlice'

// Component that listens for route changes and updates gradient
function RouteChangeHandler() {
  const dispatch = useAppDispatch();
  const routerState = useRouterState();
  const previousPath = useRef<string | null>(null);

  useEffect(() => {
    const currentPath = routerState.location.pathname;
    // Only increment on actual path changes, not initial render
    if (previousPath.current !== null && previousPath.current !== currentPath) {
      dispatch(incrementGradient());
    }
    previousPath.current = currentPath;
  }, [routerState.location.pathname, dispatch]);

  return null;
}

function RootComponent() {
  const dispatch = useAppDispatch();
  const isTourOpen = useSelector(selectIsTourOpen);
  const isUpgradeModalOpen = useSelector(selectIsUpgradeModalOpen);
  const upgradePromptContext = useSelector(selectUpgradePromptContext);

  return (
    <>
      <GradientBackground />
      <RouteChangeHandler />
      <Outlet />
      <OnboardingTour isOpen={isTourOpen} onClose={() => dispatch(closeTour())} />
      <UpgradePromptModal
        isOpen={isUpgradeModalOpen}
        onClose={() => dispatch(closeUpgradeModal())}
        context={upgradePromptContext ?? undefined}
      />
      {import.meta.env.DEV && !import.meta.env.VITE_HIDE_DEVTOOLS && <TanStackRouterDevtools position="top-left" />}
    </>
  )
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  return <RouteErrorBoundary error={error} reset={reset} />
}

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  errorComponent: RootErrorComponent,
})
